import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { landingApi, LandingInterestPayload } from '../../api/landingApi';

type SubmissionState = 'idle' | 'loading' | 'success' | 'error';

type Capability = {
  index: string;
  title: string;
  description: string;
};

type Snapshot = {
  title: string;
  summary: string;
  details: string[];
  image: string;
};

const colors = {
  background: '#ffffff',
  section: '#f7f7f7',
  card: '#ffffff',
  text: '#111111',
  muted: '#5f6368',
  border: '#e6e6e6',
  accent: '#2563eb',
  accentDark: '#1d4ed8',
  accentSoft: '#dbeafe',
  line: '#dddddd',
};

const navItems = [
  { label: 'Platform', href: '/platform' },
  { label: 'Deployments', href: '/deployments' },
  { label: 'Contact', href: '/contact' },
];

const capabilities: Capability[] = [
  {
    index: '1',
    title: 'Flexible inputs',
    description: 'Start from spreadsheets, APIs, or upstream systems without forcing a rigid data model on day one.',
  },
  {
    index: '2',
    title: 'Custom routing logic',
    description: 'Our OR scientists work with your team to build custom routing models.',
  },
  {
    index: '3',
    title: 'Operator review',
    description:
      'Planners can review inputs, analyze outcomes, and fine-tune plans through a console designed to surface inefficiencies and enable quick, intuitive edits.',
  },
];

const proofPoints = [
  'Flexible inputs',
  'Custom-built routing models',
  'Human-in-the-loop review',
  'Built for operational constraints',
];

const sectionTitleSx = {
  fontFamily: '"Space Grotesk", "IBM Plex Sans", sans-serif',
  fontWeight: 700,
  letterSpacing: '-0.04em',
  color: colors.text,
};

const bodyFontSx = {
  fontFamily: '"Inter", "IBM Plex Sans", sans-serif',
};

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const publicUrl = process.env.PUBLIC_URL || '';
  const [formData, setFormData] = useState<LandingInterestPayload>({
    name: '',
    email: '',
    company: '',
    useCase: '',
  });
  const [status, setStatus] = useState<SubmissionState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const snapshots = useMemo<Snapshot[]>(
    () => [
      {
        title: 'Waste management routing',
        summary: 'Route plans that account for regulated movement, equipment fit, and qualified driver assignment.',
        details: ['Restriction zones', 'Vehicle and container compatibility', 'Certification matching'],
        image: `${publicUrl}/resource/waste-mgt-example.png`,
      },
      {
        title: 'B2B eCommerce routing',
        summary: 'Routing that balances on-time commitments, cost pressure, and planner review before execution.',
        details: ['Time-slot SLA handling', 'Driver familiarity and route quality', 'Approval-ready outputs'],
        image: `${publicUrl}/resource/b2c-ecom-example.png`,
      },
    ],
    [publicUrl]
  );

  useEffect(() => {
    const pageConfig: Record<string, { title: string; description: string; sectionId?: string }> = {
      '/': {
        title: 'ExPlan | Customizable Vehicle Routing Software',
        description:
          'ExPlan is customizable vehicle routing software with flexible inputs, custom-built routing models, and rich review tools.',
      },
      '/platform': {
        title: 'ExPlan Platform | Flexible Inputs, Custom Routing Models, Review Tools',
        description:
          'Explore the ExPlan platform for flexible data inputs, custom routing logic, and planner review workflows.',
        sectionId: 'platform',
      },
      '/deployments': {
        title: 'ExPlan Deployments | Different Routing Solutions for Different Problems',
        description:
          'See how ExPlan supports very different routing solutions through customizable models and operational workflows.',
        sectionId: 'deployments',
      },
      '/contact': {
        title: 'Contact ExPlan | Discuss Your Routing Workflow',
        description:
          'Contact ExPlan to discuss your routing workflow, constraints, and planning requirements with the team.',
        sectionId: 'contact',
      },
    };

    const config = pageConfig[location.pathname] || pageConfig['/'];
    document.title = config.title;

    const updateMeta = (name: string, content: string) => {
      let tag = document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.name = name;
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    const updatePropertyMeta = (property: string, content: string) => {
      let tag = document.head.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };

    const canonicalUrl = `https://explan.in${location.pathname === '/' ? '' : location.pathname}`;
    let canonical = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;

    updateMeta('description', config.description);
    updatePropertyMeta('og:title', config.title);
    updatePropertyMeta('og:description', config.description);
    updatePropertyMeta('og:url', canonicalUrl);
    updateMeta('twitter:title', config.title);
    updateMeta('twitter:description', config.description);

    if (config.sectionId) {
      const sectionId = config.sectionId;
      requestAnimationFrame(() => {
        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'auto', block: 'start' });
      });
    } else {
      requestAnimationFrame(() => window.scrollTo({ top: 0, left: 0, behavior: 'auto' }));
    }
  }, [location.pathname]);

  const handleChange = (field: keyof LandingInterestPayload) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      await landingApi.submitInterest(formData);
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        useCase: '',
      });
    } catch (error: any) {
      setErrorMessage(error?.message || 'We could not notify the ExPlan team right now. Please drop a mail at hello@explan.in.');
      setStatus('error');
    }
  };

  const renderStatusAlert = () => {
    if (status === 'success') {
      return (
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          Thanks. The ExPlan team has your details and will reach out shortly.
        </Alert>
      );
    }

    if (status === 'error') {
      return (
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {errorMessage}
        </Alert>
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: colors.background,
        color: colors.text,
        ...bodyFontSx,
        '& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiFormLabel-root': bodyFontSx,
        '@keyframes routePulse': {
          '0%': { strokeDashoffset: 0, opacity: 0.35 },
          '50%': { opacity: 0.58 },
          '100%': { strokeDashoffset: -120, opacity: 0.35 },
        },
        '@keyframes floatNode': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-6px)' },
        },
        '@keyframes fadeUp': {
          from: { opacity: 0, transform: 'translateY(18px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 20,
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
          backgroundColor: alpha(colors.background, 0.92),
          backdropFilter: 'blur(14px)',
        }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ minHeight: 64 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                component="img"
                src={`${publicUrl}/transparent_logo.png`}
                alt="Decision Opt"
                sx={{ height: 68, width: 'auto' }}
              />
              <Stack spacing={0}>
                <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 22, lineHeight: 1.05 }}>
                  ExPlan
                </Typography>
                <Typography sx={{ color: colors.muted, fontSize: 12, lineHeight: 1.2 }}>
                  by DecisionOpt
                </Typography>
              </Stack>
            </Stack>
            <Stack direction="row" spacing={3} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
              {navItems.map(item => (
                <Box
                  key={item.label}
                  component="a"
                  href={item.href}
                  sx={{
                    color: colors.muted,
                    textDecoration: 'none',
                    fontSize: 14,
                    transition: 'color 180ms ease',
                    '&:hover': { color: colors.accent },
                  }}
                >
                  {item.label}
                </Box>
              ))}
              <Button
                variant="text"
                onClick={() => navigate('/login')}
                sx={{ color: colors.text, fontWeight: 600 }}
              >
                Login
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box
        component="section"
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', md: 'calc(100vh - 64px)' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.55,
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 1200 720"
            preserveAspectRatio="none"
            sx={{ width: '100%', height: '100%' }}
          >
            <path
              d="M80 530 C 220 420, 290 450, 420 330 S 650 180, 780 240 S 1020 420, 1130 280"
              fill="none"
              stroke={alpha(colors.accent, 0.43)}
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="10 14"
              style={{ animation: 'routePulse 10s linear infinite' }}
            />
            <path
              d="M120 250 C 280 180, 400 220, 520 150 S 770 120, 920 220 S 1080 320, 1140 390"
              fill="none"
              stroke={alpha(colors.text, 0.22)}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="8 18"
              style={{ animation: 'routePulse 14s linear infinite reverse' }}
            />
            <path
              d="M18 628 C 126 590, 224 612, 316 574 S 470 550, 592 586 S 790 644, 1008 602"
              fill="none"
              stroke={alpha(colors.accent, 0.37)}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="7 16"
              style={{ animation: 'routePulse 16s linear infinite' }}
            />
            <path
              d="M786 56 C 860 84, 924 66, 996 92 S 1092 126, 1182 88"
              fill="none"
              stroke={alpha(colors.text, 0.2)}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="6 15"
              style={{ animation: 'routePulse 18s linear infinite reverse' }}
            />
            <path
              d="M6 96 C 82 126, 152 94, 214 124 S 304 154, 372 142"
              fill="none"
              stroke={alpha(colors.accent, 0.35)}
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="6 14"
              style={{ animation: 'routePulse 20s linear infinite' }}
            />
            <path
              d="M936 640 C 1014 610, 1070 624, 1128 598 S 1170 554, 1194 520"
              fill="none"
              stroke={alpha(colors.text, 0.18)}
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray="5 14"
              style={{ animation: 'routePulse 22s linear infinite reverse' }}
            />
            {[
              { cx: 120, cy: 250, delay: '0s' },
              { cx: 420, cy: 330, delay: '0.8s' },
              { cx: 780, cy: 240, delay: '0.4s' },
              { cx: 1130, cy: 280, delay: '1.2s' },
              { cx: 920, cy: 220, delay: '0.2s' },
              { cx: 74, cy: 608, delay: '0.6s' },
              { cx: 316, cy: 574, delay: '1s' },
              { cx: 592, cy: 586, delay: '0.1s' },
              { cx: 1008, cy: 602, delay: '1.4s' },
              { cx: 786, cy: 56, delay: '0.5s' },
              { cx: 996, cy: 92, delay: '1.1s' },
              { cx: 20, cy: 102, delay: '0.9s' },
              { cx: 214, cy: 124, delay: '0.3s' },
              { cx: 1128, cy: 598, delay: '0.7s' },
            ].map(node => (
              <g key={`${node.cx}-${node.cy}`}>
                <circle
                  cx={node.cx}
                  cy={node.cy}
                  r="11"
                  fill={alpha(
                    colors.accent,
                    [74, 316, 592, 1008, 786, 996, 20, 214, 1128].includes(node.cx) ? 0.14 : 0.09
                  )}
                />
                <circle
                  cx={node.cx}
                  cy={node.cy}
                  r="4"
                  fill={alpha(
                    colors.accent,
                    [74, 316, 592, 1008, 786, 996, 20, 214, 1128].includes(node.cx) ? 0.72 : 0.58
                  )}
                  style={{ animation: `floatNode 3.6s ease-in-out ${node.delay} infinite` }}
                />
              </g>
            ))}
          </Box>
        </Box>

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, py: { xs: 10, md: 14 } }}>
          <Stack spacing={3} alignItems="center" textAlign="center" sx={{ animation: 'fadeUp 700ms ease forwards' }}>
            <Typography
              sx={{
                color: colors.accent,
                textTransform: 'uppercase',
                letterSpacing: '0.18em',
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              ExPlan Routing OS
            </Typography>
            <Typography
              variant="h1"
              sx={{
                ...sectionTitleSx,
                fontSize: { xs: '2.75rem', md: '4.8rem' },
                lineHeight: 1.02,
                maxWidth: 980,
              }}
            >
              Customizable vehicle routing software built to handle your unique constraints
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: '1rem', md: '1.2rem' },
                color: colors.muted,
                lineHeight: 1.75,
                maxWidth: 760,
              }}
            >
              Flexible inputs, custom-built routing models, and rich review tools designed to seamlessly combine human
              judgment with machine intelligence
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                href="/contact"
                sx={{
                  px: 4,
                  py: 1.6,
                  borderRadius: 2,
                  fontWeight: 700,
                  bgcolor: colors.accent,
                  color: '#ffffff',
                  boxShadow: 'none',
                  '&:hover': { bgcolor: colors.accentDark, boxShadow: 'none' },
                }}
              >
                Request a walkthrough
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/login')}
                sx={{
                  px: 4,
                  py: 1.6,
                  borderRadius: 2,
                  fontWeight: 700,
                  color: colors.accent,
                  borderColor: colors.accent,
                  '&:hover': {
                    borderColor: colors.accentDark,
                    backgroundColor: alpha(colors.accent, 0.04),
                  },
                }}
              >
                Login
              </Button>
            </Stack>
            <Stack direction="row" flexWrap="wrap" justifyContent="center" spacing={1.2} useFlexGap sx={{ pt: 1 }}>
              {proofPoints.map(item => (
                <Box
                  key={item}
                  sx={{
                    px: 1.8,
                    py: 0.9,
                    borderRadius: 999,
                    border: `1px solid ${colors.border}`,
                    bgcolor: alpha('#fff', 0.55),
                    fontSize: 13,
                    color: colors.muted,
                  }}
                >
                  {item}
                </Box>
              ))}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box component="section" id="platform" sx={{ py: { xs: 9, md: 12 }, bgcolor: colors.section }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Typography sx={{ color: colors.accent, textTransform: 'uppercase', letterSpacing: '0.16em', fontSize: 12, fontWeight: 700 }}>
              Platform
            </Typography>
            <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: '2rem', md: '3.2rem' }, mt: 1.5 }}>
              Built for challenges that are unique to your problem and need special handling with custom insights.
            </Typography>
            <Typography sx={{ maxWidth: 700, mx: 'auto', mt: 2, color: colors.muted, lineHeight: 1.75 }}>
              ExPlan is meant for operations where standard route planning breaks down and custom modeling, custom data
              handling, and planner oversight become necessary.
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {capabilities.map(item => (
              <Grid item xs={12} md={4} key={item.title}>
                <Box
                  sx={{
                    position: 'relative',
                    pt: 4.5,
                    textAlign: 'center',
                    transition: 'transform 220ms ease, box-shadow 220ms ease',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: colors.background,
                      border: `4px solid ${colors.section}`,
                      color: colors.accent,
                      fontFamily: '"Space Grotesk", sans-serif',
                      fontWeight: 700,
                      fontSize: 28,
                    }}
                  >
                    {item.index}
                  </Box>
                  <Paper
                    elevation={0}
                    sx={{
                      height: '100%',
                      px: 3,
                      pt: 6,
                      pb: 3.5,
                      borderRadius: 2.5,
                      border: `1px solid ${alpha('#000', 0.08)}`,
                      bgcolor: colors.card,
                      boxShadow: '0 12px 28px rgba(0,0,0,0.04)',
                    }}
                  >
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 24 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ mt: 1.5, color: colors.muted, lineHeight: 1.75 }}>
                      {item.description}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" id="deployments" sx={{ py: { xs: 9, md: 12 } }}>
        <Container maxWidth="lg">
          <Box textAlign="center" sx={{ mb: 8 }}>
            <Typography sx={{ color: colors.accent, textTransform: 'uppercase', letterSpacing: '0.16em', fontSize: 12, fontWeight: 700 }}>
              Deployments
            </Typography>
            <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: '2rem', md: '3.2rem' }, mt: 1.5 }}>
              Built for very different routing realities
            </Typography>
            <Typography sx={{ maxWidth: 720, mx: 'auto', mt: 2, color: colors.muted, lineHeight: 1.75 }}>
              Two distinct routing scenarios demonstrating how the platform adapts to very different operational needs
            </Typography>
          </Box>
          <Grid container spacing={4}>
            {snapshots.map(snapshot => (
              <Grid item xs={12} md={6} key={snapshot.title}>
                <Paper
                  elevation={0}
                  sx={{
                    height: '100%',
                    overflow: 'hidden',
                    borderRadius: 2.5,
                    border: `1px solid ${alpha('#000', 0.08)}`,
                    bgcolor: colors.card,
                    boxShadow: '0 12px 28px rgba(0,0,0,0.04)',
                  }}
                >
                  <Box
                    component="img"
                    src={snapshot.image}
                    alt={snapshot.title}
                    loading="lazy"
                    sx={{ width: '100%', height: 240, objectFit: 'cover', bgcolor: '#ece8df' }}
                  />
                  <Box sx={{ p: 3 }}>
                    <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 24 }}>
                      {snapshot.title}
                    </Typography>
                    <Typography sx={{ mt: 1.5, color: colors.muted, lineHeight: 1.75 }}>
                      {snapshot.summary}
                    </Typography>
                    <Stack spacing={1.2} sx={{ mt: 2.5 }}>
                      {snapshot.details.map(detail => (
                        <Box key={detail} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                          <Box
                            sx={{
                              mt: 0.8,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: colors.accent,
                              flexShrink: 0,
                            }}
                          />
                          <Typography sx={{ color: colors.muted }}>{detail}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" id="contact" sx={{ py: { xs: 9, md: 12 }, bgcolor: colors.section }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 2.5,
              border: `1px solid ${alpha('#000', 0.08)}`,
              bgcolor: colors.card,
              boxShadow: '0 14px 34px rgba(0,0,0,0.05)',
            }}
          >
            <Box textAlign="center" sx={{ mb: 4 }}>
              <Typography sx={{ color: colors.accent, textTransform: 'uppercase', letterSpacing: '0.16em', fontSize: 12, fontWeight: 700 }}>
                Contact
              </Typography>
              <Typography variant="h2" sx={{ ...sectionTitleSx, fontSize: { xs: '2rem', md: '3rem' }, mt: 1.5 }}>
                Tell us what your routing workflow needs to support.
              </Typography>
              <Typography sx={{ maxWidth: 640, mx: 'auto', mt: 2, color: colors.muted, lineHeight: 1.75 }}>
                Share your use case and the ExPlan team will follow up directly. Prefer email? Reach us at
                {' '}
                <strong>hello@explan.in</strong>.
              </Typography>
              <Typography sx={{ maxWidth: 640, mx: 'auto', mt: 1.5, color: colors.muted, lineHeight: 1.7, fontSize: 14 }}>
                If the form does not go through, drop a mail at hello@explan.in with your routing use case.
              </Typography>
            </Box>
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Full name"
                    required
                    value={formData.name}
                    onChange={handleChange('name')}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#ffffff',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Work email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange('email')}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#ffffff',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Company name"
                    required
                    value={formData.company}
                    onChange={handleChange('company')}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#ffffff',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="What are you routing today?"
                    multiline
                    minRows={4}
                    value={formData.useCase}
                    onChange={handleChange('useCase')}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#ffffff',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Stack spacing={2} alignItems="center">
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={status === 'loading'}
                      sx={{
                        px: 4,
                        py: 1.4,
                        borderRadius: 2,
                        fontWeight: 700,
                        bgcolor: colors.accent,
                        color: '#ffffff',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: colors.accentDark, boxShadow: 'none' },
                      }}
                    >
                      {status === 'loading' ? 'Submitting...' : 'Request a walkthrough'}
                    </Button>
                    {renderStatusAlert()}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Box component="footer" sx={{ borderTop: `1px solid ${alpha('#000', 0.06)}`, py: 4 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1.25} alignItems="center">
                <Box
                  component="img"
                  src={`${publicUrl}/transparent_logo.png`}
                  alt="Decision Opt"
                  sx={{ height: 60, width: 'auto' }}
                />
                <Stack spacing={0}>
                  <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 21, lineHeight: 1.05 }}>
                    ExPlan
                  </Typography>
                  <Typography sx={{ color: colors.muted, fontSize: 12, lineHeight: 1.2 }}>
                    by DecisionOpt
                  </Typography>
                </Stack>
              </Stack>
              <Typography sx={{ mt: 1.5, color: colors.muted, fontSize: 13, lineHeight: 1.7 }}>
                Custom vehicle routing software built for operations that need configurable inputs, custom logic, and
                human review before execution.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography sx={{ fontFamily: '"Space Grotesk", sans-serif', fontWeight: 700, fontSize: 16 }}>
                Contact
              </Typography>
              <Typography sx={{ mt: 1.5, color: colors.muted, fontSize: 13, lineHeight: 1.8 }}>
                hello@explan.in
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 13, lineHeight: 1.8 }}>
                DecisionOpt Consulting Pvt. Ltd.
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 13, lineHeight: 1.8 }}>
                Existing users can continue to log in from this page.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
